// Compact picker shared by the cross-sample verbs (sidechain, convolve,
// stretch). Two ways to choose audio:
//   1. Pick from the user's IDB sample library (most common — these
//      verbs are about combining things you already have).
//   2. Upload a file fresh from disk.
//
// Returns the resolved AudioBuffer + a display name + provenance (the
// sample id if it came from IDB, so the calling verb can record it).

import { useCallback, useRef, useState } from 'react';
import { useSampleStore } from '../../blocks/library/SampleStoreContext';
import { decodeAudioFile } from '../import';
import type { Sample } from '../types';

export type PickedSource = {
  buffer: AudioBuffer;
  name: string;
  /** Set when the source came from IDB. Records provenance for the
   *  produced sample's `sourceSampleIds`. */
  sampleId?: string;
};

export type SourcePickerProps = {
  audioContext: AudioContext | null;
  /** Currently-picked sample id, for the dropdown's selected option. */
  selectedId?: string;
  /** Caller logs status (consistent with the verb shells). */
  onStatus: (message: string, level?: 'ok' | 'warn' | 'err' | '') => void;
  onPicked: (picked: PickedSource) => void;
  /** Optional label shown above the picker. */
  label?: string;
};

export function SourcePicker({
  audioContext,
  selectedId,
  onStatus,
  onPicked,
  label,
}: SourcePickerProps) {
  const { samples, storage } = useSampleStore();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Monotonically-increasing token. Each pick increments it; when a
  // decode resolves we drop the result if a newer pick has started in
  // the meantime. Without this, picking A then B before A's decode
  // finishes can land A's buffer in state after B's.
  const pickSeq = useRef(0);
  const [decoding, setDecoding] = useState(false);

  const loadFromIdb = useCallback(
    async (sample: Sample) => {
      if (!storage || !audioContext) {
        onStatus('engine not booted — press Play first', 'warn');
        return;
      }
      const myToken = ++pickSeq.current;
      setDecoding(true);
      try {
        const blob = await storage.getBlob(sample.storageKey);
        if (myToken !== pickSeq.current) return;
        if (!blob) {
          onStatus(`Blob missing for ${sample.name}.`, 'err');
          return;
        }
        const buffer = await audioContext.decodeAudioData(
          await blob.arrayBuffer(),
        );
        if (myToken !== pickSeq.current) return;
        onPicked({ buffer, name: sample.name, sampleId: sample.id });
        onStatus(`Loaded ${sample.name}.`, 'ok');
      } catch (e) {
        if (myToken !== pickSeq.current) return;
        onStatus(
          'Decode failed: ' + (e instanceof Error ? e.message : String(e)),
          'err',
        );
      } finally {
        if (myToken === pickSeq.current) setDecoding(false);
      }
    },
    [storage, audioContext, onPicked, onStatus],
  );

  const loadFromFile = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !audioContext) return;
      const f = files.item(0);
      if (!f) return;
      const myToken = ++pickSeq.current;
      setDecoding(true);
      try {
        const result = await decodeAudioFile(f, audioContext);
        if (myToken !== pickSeq.current) return;
        if (!result.ok) {
          onStatus(`Load rejected: ${result.reason}`, 'err');
          return;
        }
        onPicked({ buffer: result.buffer, name: result.suggestedName });
        onStatus(`Loaded ${result.filename}.`, 'ok');
      } finally {
        if (myToken === pickSeq.current) setDecoding(false);
      }
    },
    [audioContext, onPicked, onStatus],
  );

  return (
    <div className="flex flex-col gap-1.5">
      {label !== undefined && (
        <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">
          {label}
        </div>
      )}
      <div className="flex items-center gap-2">
        <select
          value={selectedId ?? ''}
          disabled={decoding}
          onChange={(e) => {
            const id = e.target.value;
            const found = samples.find((s) => s.id === id);
            if (found) void loadFromIdb(found);
          }}
          className="flex-1 bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-[11px] text-zinc-100 focus:border-yellow-300 focus:outline-none rounded-sm disabled:opacity-50"
        >
          <option value="" disabled>
            {decoding ? 'Decoding…' : 'Pick a sample…'}
          </option>
          {samples.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} · {(s.durationMs / 1000).toFixed(1)}s
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={decoding}
          className="px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] bg-zinc-900 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
        >
          File…
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          style={{ display: 'none' }}
          onChange={(e) => void loadFromFile(e.target.files)}
        />
      </div>
    </div>
  );
}
